/**
 * Bookings tab for the new console (Traci's review, 23 Sep 2026; prototype v108 §3.1). One row per booking: client,
 * location, package, event date, booking date, status (Pending until event day, Post after, Completed and Cancelled
 * under Archived). Expand a row for the lifecycle bar, the booking details and the talent; Open Full Booking for
 * everything else. Every booking of a client lives here, so an anniversary shoot never mixes with the wedding.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, LayoutGrid, List, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/admin/ui/input';
import { filterRows, groupByStage } from '@/lib/staging/bookingsList';
import { STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch, shortDate } from '../api';
import LifecycleTrack from '../components/LifecycleTrack';
import { useStaging } from '../StagingLayout';

const STATUS_TONE: Record<string, string> = {
  Pending: 'border-amber-300 bg-amber-50 text-amber-900',
  Post: 'border-purple-300 bg-purple-50 text-purple-900',
  Completed: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  Cancelled: 'border-slate-300 bg-slate-50 text-slate-700'
};

export function StatusPill({ status }: { status: string }) {
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[status] || ''}`}>{status}</span>;
}

const TAB_LABEL = { active: 'Active', archived: 'Archived', rose: 'Rose Intakes' } as const;

/** Logistics questionnaire state on each row (Laura, 29 Sep 2026). */
const Q_LABEL: Record<string, string> = { submitted: 'Submitted', in_progress: 'Started', not_started: 'Not started' };
const Q_TONE: Record<string, string> = { submitted: 'text-emerald-700', in_progress: 'text-amber-700', not_started: 'text-rose-700' };
const Q_FILTERS = ['', 'not_submitted', 'not_started', 'in_progress', 'submitted'];
const Q_FILTER_LABEL: Record<string, string> = { '': 'Any', not_submitted: 'Not submitted', not_started: 'Not started', in_progress: 'Started, not submitted', submitted: 'Submitted' };

/**
 * Rose Intakes (folded in from the current console's Bookings tab, 26 Sep 2026): newest submission first; the row
 * opens the Full Booking. Payment and amount paid arrive only for Admin and Billing.
 */
function RoseTable({ rows }: { rows: any[] }) {
  const money = rows.some((r) => r.rose?.payment !== undefined);
  const cols = money ? 'grid-cols-[100px_minmax(0,1.2fr)_minmax(0,1.2fr)_110px_minmax(0,1fr)_110px_minmax(0,1fr)]' : 'grid-cols-[100px_minmax(0,1.2fr)_minmax(0,1.2fr)_110px_minmax(0,1fr)_110px]';
  const usd = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <p className="border-b px-4 py-2 text-xs text-muted-foreground">Rose waitlist and booking intakes, newest submission first. Open one for the full record.</p>
      <div className={`grid ${cols} gap-3 border-b px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground`}>
        <span>Submitted</span><span>Client</span><span>Contact</span><span>Wedding Date</span><span>Location</span><span>Type</span>{money && <span>Payment</span>}
      </div>
      {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No Rose intakes match.</p>}
      <div className="divide-y">
        {rows.map((r: any) => (
          <Link key={r.id} to={`${STAGING_ROOT}/bookings/${r.id}`} className={`grid ${cols} items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/40`}>
            <span className="tabular-nums text-muted-foreground">{shortDate(r.booking_date)}</span>
            <span className="min-w-0"><span className="block truncate font-medium">{r.client_name || r.rose.name || 'Client'}</span>{r.rose.partner && <span className="block truncate text-xs text-muted-foreground">with {r.rose.partner}</span>}</span>
            <span className="min-w-0 text-xs"><span className="block truncate">{r.rose.email || '—'}</span>{r.rose.phone && <span className="block truncate text-muted-foreground">{r.rose.phone}</span>}</span>
            <span className="tabular-nums">{r.rose.requested_date ? shortDate(r.rose.requested_date) : '—'}</span>
            <span className="truncate text-muted-foreground">{r.rose.location || '—'}{r.rose.registry && <span className="block truncate text-xs">Registry {r.rose.registry}</span>}</span>
            <span><span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-800">{r.rose.type}</span></span>
            {money && <span className="text-xs"><span className="block">{r.rose.payment}</span>{r.rose.amount_paid_cents > 0 && <span className="block text-muted-foreground">Paid {usd(r.rose.amount_paid_cents)}</span>}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function BookingsPage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const sort = params.get('sort') === 'booking' ? 'booking' : 'event';
  // Traci's doc: the Questionnaire filter is for Admin and Sales only (it follows View As)
  const { role } = useStaging();
  const tab = tabParam === 'archived' || tabParam === 'rose' ? tabParam : 'active';
  const showQFilter = role === 'admin' || role === 'sales';
  const qFilter = showQFilter && Q_FILTERS.includes(params.get('questionnaire') || '') ? (params.get('questionnaire') || '') : '';
  const view = params.get('view') === 'cards' ? 'cards' : 'list';
  const [q, setQ] = useState(params.get('q') || '');
  const [open, setOpen] = useState<string | null>(params.get('booking'));
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { stagingFetch('/api/admin/staging/bookings').then(setData).catch((e) => setError(e.message || String(e))); }, []);
  const set = (k: string, v: string | null) => { const next = new URLSearchParams(params); if (v) next.set(k, v); else next.delete(k); setParams(next, { replace: true }); };

  const rows = useMemo(() => (data ? filterRows(data.rows, { tab, q, sort, questionnaire: tab === 'rose' ? '' : qFilter, today: data.today }) : []), [data, tab, q, sort, qFilter]);
  const counts = useMemo(() => (data ? { active: filterRows(data.rows, { tab: 'active' }).length, archived: filterRows(data.rows, { tab: 'archived' }).length, rose: filterRows(data.rows, { tab: 'rose' }).length } : { active: 0, archived: 0, rose: 0 }), [data]);

  return (
    <div className="p-8 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Bookings</h2>
          <p className="text-sm text-muted-foreground mt-1">One row per booking. Pending until the event day, Post after it; Completed and Cancelled bookings move to Archived and stay fully open.</p>
        </div>
        <div className="flex gap-1.5" role="group" aria-label="View">
          <button type="button" aria-pressed={view === 'list'} onClick={() => set('view', null)} className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm ${view === 'list' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-accent'}`}><List className="h-4 w-4" /> List</button>
          <button type="button" aria-pressed={view === 'cards'} onClick={() => set('view', 'cards')} className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm ${view === 'cards' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-accent'}`}><LayoutGrid className="h-4 w-4" /> Cards / Lifecycle</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5" role="tablist" aria-label="Bookings">
          {(['active', 'archived', 'rose'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => set('tab', t === 'active' ? null : t)}
              className={`rounded-full border px-3 py-1 text-sm ${tab === t ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>{TAB_LABEL[t]} <span className="tabular-nums opacity-80">{counts[t]}</span></button>
          ))}
        </div>
        <label className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" value={q} onChange={(e) => { setQ(e.target.value); set('q', e.target.value || null); }} placeholder="Client, booking ID, location, package, talent…" aria-label="Search bookings" />
        </label>
        {tab !== 'rose' && showQFilter && <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Questionnaire</span>
          <select aria-label="Questionnaire" className="h-9 rounded-md border bg-background px-2 text-sm" value={qFilter} onChange={(e) => set('questionnaire', e.target.value || null)}>
            {Q_FILTERS.map((k) => <option key={k} value={k}>{Q_FILTER_LABEL[k]}</option>)}
          </select>
        </label>}
        {tab !== 'rose' && <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Sort by</span>
          <select aria-label="Sort by" className="h-9 rounded-md border bg-background px-2 text-sm" value={sort} onChange={(e) => set('sort', e.target.value === 'booking' ? 'booking' : null)}>
            <option value="event">Event date</option>
            <option value="booking">Booking date</option>
          </select>
        </label>}
      </div>

      {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
      {!data && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      {data && tab === 'rose' && <RoseTable rows={rows} />}

      {data && tab !== 'rose' && view === 'cards' && (
        rows.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No bookings match these filters.</p> : (
          <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="Bookings by lifecycle stage">
            {groupByStage(rows).map((col) => (
              <section key={col.key} className="min-h-[190px] rounded-xl border bg-muted/30">
                <div className="flex items-center justify-between gap-2 border-b px-3 py-2"><h3 className="text-sm font-semibold leading-snug">{col.label}</h3><span className="rounded-full border bg-card px-2 text-xs tabular-nums">{col.count}</span></div>
                <div className="max-h-[60vh] space-y-2 overflow-y-auto p-2">
                  {col.rows.map((r: any) => (
                    <Link key={r.id} to={`${STAGING_ROOT}/bookings/${r.id}`} className="block rounded-lg border bg-card p-3 text-sm shadow-sm hover:bg-accent/40">
                      <div className="font-medium">{r.client_name || 'Client'}</div>
                      <div className="mt-1 text-xs text-muted-foreground tabular-nums">{shortDate(r.event_date)}{r.location ? ` · ${r.location}` : ''}</div>
                      <div className="mt-1 text-xs">{r.package}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{r.talent.length ? r.talent.map((t: any) => t.name || t.role_label).join(', ') : 'No talent assigned yet'}</div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )
      )}

      {data && tab !== 'rose' && view === 'list' && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="grid grid-cols-[28px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_110px_110px_100px_100px] gap-3 border-b px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <span></span><span>Client</span><span>Event Location</span><span>Package</span><span>Event Date</span><span>Booking Date</span><span>Questionnaire</span><span>Status</span>
          </div>
          {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No bookings match these filters.</p>}
          <div className="divide-y">
            {rows.map((r: any) => {
              const expanded = open === r.id;
              return (
                <div key={r.id}>
                  <div className="grid grid-cols-[28px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.2fr)_110px_110px_100px_100px] items-center gap-3 px-4 py-2.5 text-sm">
                    <button type="button" aria-label={expanded ? 'Collapse' : 'Expand'} aria-expanded={expanded} onClick={() => { setOpen(expanded ? null : r.id); set('booking', expanded ? null : r.id); }} className="text-muted-foreground hover:text-foreground">
                      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <span className="min-w-0 truncate">{r.client_id ? <Link className="font-medium hover:underline" to={`${STAGING_ROOT}/clients/${r.client_id}`}>{r.client_name || 'Client'}</Link> : (r.client_name || '—')}</span>
                    <span className="truncate text-muted-foreground">{r.location || '—'}</span>
                    <span className="truncate">{r.package}</span>
                    <span className="tabular-nums">{shortDate(r.event_date)}</span>
                    <span className="tabular-nums text-muted-foreground">{shortDate(r.booking_date)}</span>
                    <span className={`text-xs font-medium ${Q_TONE[r.questionnaire] || ''}`}>{Q_LABEL[r.questionnaire] || '—'}</span>
                    <span><StatusPill status={r.status} /></span>
                  </div>
                  {expanded && (
                    <div className="space-y-2.5 bg-muted/30 pb-4 pl-12 pr-4">
                      <div className="rounded-lg border bg-card p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold">Booking Lifecycle</div>
                            <div className="text-xs text-muted-foreground">Current stage: <b className="text-foreground">{r.current_label}</b></div>
                          </div>
                          <Link to={`${STAGING_ROOT}/bookings/${r.id}`} className="shrink-0 rounded-md border px-3 py-1.5 text-sm hover:bg-accent">Open Full Booking →</Link>
                        </div>
                        <div className="mt-3"><LifecycleTrack stages={r.stages} /></div>
                      </div>
                      <div className="grid gap-2.5 lg:grid-cols-[1.4fr_0.8fr]">
                        <div className="rounded-lg border bg-card p-3 text-sm">
                          <div className="font-semibold">Booking Details</div>
                          <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            <div className="rounded-md border p-2"><span className="block text-[10px] uppercase text-muted-foreground">Booking ID</span><b className="mt-1 block font-mono text-xs">{r.id.slice(0, 8).toUpperCase()}</b></div>
                            <div className="rounded-md border p-2"><span className="block text-[10px] uppercase text-muted-foreground">Coverage</span><b className="mt-1 block text-xs">{r.package}</b></div>
                            <div className="rounded-md border p-2"><span className="block text-[10px] uppercase text-muted-foreground">Add-ons</span><b className="mt-1 block text-xs">{r.addons.length ? r.addons.join(', ') : 'None'}</b></div>
                          </div>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-sm">
                          <div className="font-semibold">Associated Talent</div>
                          {r.talent.length === 0 ? <p className="mt-2 text-xs text-muted-foreground">No talent assigned yet.</p> : (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              {r.talent.map((t: any) => (
                                <div key={t.role} className="rounded-md border p-2">
                                  <span className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground">{t.role_label}</span>
                                  {t.talent_id ? <Link className="text-xs font-bold hover:underline" to={`${STAGING_ROOT}/talent/${t.talent_id}`}>{t.name || 'Talent'}</Link> : <b className="text-xs">{t.name || 'Talent'}</b>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
